import { Button } from '@iwoca/orion';

import { UnavailableLimit } from './UnavailableLimit';
import { UpdateTurnover } from './UpdateTurnover';
import {
  SpendingLimitEntry,
  TSpendingLimitType,
} from '../../../hooks/useGetSpendingLimits';
import { useUpdateTurnover } from '../../../hooks/useUpdateTurnover';
import { formatCurrency } from '../../../utils/currency';
import { formatDuration } from '../../SpendingLimitCard/components/duration.helpers';

const ALL_LIMIT_TYPES: TSpendingLimitType[] = ['THREE_MONTHS', 'TWELVE_MONTHS'];

const durationToMonths: Record<TSpendingLimitType, number> = {
  THIRTY_DAYS: 1,
  THREE_MONTHS: 3,
  TWELVE_MONTHS: 12,
};

export const ExpiredLimitDrawer = ({
  spendingLimits,
  refreshSpendingLimit,
  loadingRefreshSpendingLimit,
  isError,
}: {
  spendingLimits: { isValid: boolean; limits: SpendingLimitEntry[] };
  refreshSpendingLimit: () => Promise<void>;
  loadingRefreshSpendingLimit: boolean;
  isError: boolean;
}) => {
  const hasThirtyDaysLimit = spendingLimits.limits.some(
    (l) => l.duration === 'THIRTY_DAYS',
  );
  const { isTurnoverExpired } = useUpdateTurnover();

  return (
    <>
      <div className="mb-xl flex flex-col gap-xl">
        {ALL_LIMIT_TYPES.map((limitType) => {
          const spendingLimit = spendingLimits.limits.find(
            (l) => l.duration === limitType,
          );

          if (!spendingLimit) {
            return (
              <UnavailableLimit sellerProduct={limitType} key={limitType} />
            );
          }

          return (
            <div key={limitType}>
              <div className="text-base mb-s">
                {formatDuration({
                  duration: durationToMonths[spendingLimit.duration],
                  groupSameLimits: hasThirtyDaysLimit,
                })}{' '}
                purchases
              </div>
              <div className="mb-xs text-xs font-reg">Left to spend:</div>
              <div className="mb-xs flex items-start gap-s">
                <span className="text-xl">
                  {formatCurrency(spendingLimit.availableAmount)}
                </span>
                <span className="rounded-full bg-yellow-100 py-0.5 font-medium text-yellow-800 inline-flex items-center text-xs">
                  Estimated
                </span>
              </div>
              <div className="text-xs font-reg">
                Estimated spending limit:{' '}
                {formatCurrency(spendingLimit.maxAmount)}
              </div>
            </div>
          );
        })}
        <div>
          {isTurnoverExpired && (
            <UpdateTurnover
              refreshSpendingLimit={refreshSpendingLimit}
              loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
            />
          )}
          {!isTurnoverExpired && (
            <Button
              disabled={loadingRefreshSpendingLimit}
              onClick={refreshSpendingLimit}
              className="w-full"
            >
              {loadingRefreshSpendingLimit ? (
                <>Checking limits...</>
              ) : (
                <>Secure my limits</>
              )}
            </Button>
          )}
          {isError && (
            <div className="mt-2 text-center text-error">
              Something went wrong
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-s border-t border-structure-80 pt-l text-xs font-reg">
        <div>
          *Your spending limits are subject to approval and reassessed every 30
          days &mdash; if something changes with your business the amounts might
          change.
        </div>
      </div>
    </>
  );
};
